import React, { useState } from 'react';
import { Avatar, Typography, Box, Divider, IconButton } from '@mui/joy';

/* Icons */
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ChatIcon from '@mui/icons-material/Chat';
import RepeatIcon from '@mui/icons-material/Repeat';
import SendIcon from '@mui/icons-material/Send';

{/* PDF Viewer */ }
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import "react-pdf/dist/esm/Page/TextLayer.css";
import { useSelectedUserAccount } from '@/contexts/SelectedUserAccountContext';
import { sanitizePost } from "@/utils/sanitizePost";

const convertTextToHTML = (text) => {
    // Split the text into segments at double line breaks to identify paragraphs or multiple line breaks
    const segments = text.split('\n\n');
    return segments.map((segment, index) => {
        // Check if the segment is just empty space (i.e., multiple line breaks with no text in between)
        if (segment.trim() === '') {
            // For purely empty segments, return a <br /> for each extra line break to visually represent them
            return <div key={index} style={{ marginBottom: '6em' }}></div>;
        } else {
            // For segments with text, handle as before, replacing single line breaks within paragraphs with <br />
            return (
                <p key={index}>
                    {segment.split('\n').map((line, lineIndex, arr) => (
                        <React.Fragment key={lineIndex}>
                            {line}
                            {lineIndex < arr.length - 1 && <br />}
                        </React.Fragment>
                    ))}
                </p>
            );
        }
    });
};

const PreviewPost = ({ session, content, postMedia, linkPreview, deviceView }) => {
    const [numPages, setNumPages] = useState();
    const [pageNumber, setPageNumber] = useState(1);
    const [isHovering, setIsHovering] = useState(false);
    const [showFullContent, setShowFullContent] = useState(false);
    const { selectedUser } = useSelectedUserAccount();
    const cleanedContent = sanitizePost(content);


    const toggleContent = () => {
        setShowFullContent(!showFullContent);
    };

    const displayContent = () => {
        // If showing full content or content is within limit, return full content
        if (showFullContent || cleanedContent.length <= 200) {
            return (
                <>
                    {convertTextToHTML(cleanedContent)}
                    {cleanedContent.length > 200 && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Typography component="span" sx={{ cursor: 'pointer', color: '#7a7a7f', marginLeft: '8px', mb: 2 }} onClick={toggleContent}>
                                [See Less]
                            </Typography>
                        </Box>
                    )}
                </>
            );
        }

        // If content is longer than 200 characters and not showing full content, show truncated version
        const truncatedContent = cleanedContent.substring(0, 200).trim() + '...';
        return (
            <>
                {convertTextToHTML(truncatedContent)}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Typography component="span" sx={{ cursor: 'pointer', color: '#7a7a7f', marginLeft: '8px', mb: 2, align: 'right' }} onClick={toggleContent}>
                        [...See More]
                    </Typography>
                </Box>
            </>
        );
    };

    const handleMouseEnter = () => setIsHovering(true);
    const handleMouseLeave = () => setIsHovering(false);

    const goToPrevPage = () => {
        setPageNumber(pageNumber > 1 ? pageNumber - 1 : 1);
    };

    const goToNextPage = () => {
        setPageNumber(pageNumber < numPages ? pageNumber + 1 : numPages);
    };

    function onDocumentLoadSuccess({ numPages }) {
        setNumPages(numPages);
    }

    // Conditional styles based on the current device view
    const viewerContainerStyle2 = {
        // Default styles for 'desktop'
        width: deviceView === 'desktop' ? '100%' : deviceView === 'tablet' ? '90%' : '77%', // Example adjustment
        margin: 'auto',
        transition: 'width 0.5s ease',
        // Add other styles as necessary
    };

    return (
        <Box sx={{ width: '100%', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', p: 1 }} >

            <Box className='border-draw-preview' sx={{ padding: 0, ...viewerContainerStyle2, maxHeight: '90vh', overflowY: 'auto' }}>
                <Box sx={{ width: '100%' }}>
                    <Box sx={{ marginTop: 1, padding: '0px 15px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Avatar
                                sx={{ width: '3rem', height: '3rem', marginRight: '1rem' }}
                                src={selectedUser?.profile_image || ''}
                                alt="User Avatar"
                            />
                            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <Typography sx={{ fontSize: '.875rem', fontWeight: '700', marginTop: '10px' }}>
                                    {selectedUser?.name || session?.session?.user?.name}
                                </Typography>
                            </Box>


                        </Box>
                    </Box>

                    <Box sx={{ marginTop: 1, padding: '0px 15px' }}>
                        <div style={{ textAlign: 'left', fontSize: '.875rem', display: 'inline' }}>
                            {displayContent()}
                        </div>
                    </Box>

                    {/* Render Photos */}
                    {postMedia?.photos?.length > 0 && (
                        <Box sx={{ marginTop: 1 }}>
                            {/* Display the first photo larger and individually */}
                            <Box sx={{ width: '100%', marginBottom: '0rem' }}>
                                <img
                                    src={process.env.NEXT_PUBLIC_AWS_URL + postMedia.photos[0]}
                                    alt="Post Media"
                                    style={{ width: '100%', height: 'auto' }}
                                />
                            </Box>

                            {/* Display the remaining photos side by side */}
                            {postMedia.photos.length > 1 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.1rem' }}>
                                    {postMedia.photos.slice(1).map((photo, index) => (
                                        <Box key={index} sx={{ flex: '1 0 48%', height: 'auto' }}>
                                            <img
                                                src={process.env.NEXT_PUBLIC_AWS_URL + photo}
                                                alt="Post Media"
                                                style={{ width: '100%', height: 'auto' }}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Render Videos */}
                    {postMedia?.video && (
                        <Box sx={{ marginTop: 1 }}>
                            <Box sx={{ width: '100%', marginBottom: '0rem' }}>
                                <video
                                    src={process.env.NEXT_PUBLIC_AWS_URL + postMedia.video}
                                    controls
                                    style={{ width: '100%', height: 'auto' }}
                                />
                            </Box>
                        </Box>
                    )}

                    {/* Render PDFs */}
                    {postMedia?.pdf && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '1rem' }}>
                            <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} style={viewerContainerStyle}>
                                <Document
                                    file={process.env.NEXT_PUBLIC_AWS_URL + postMedia.pdf}
                                    onLoadSuccess={onDocumentLoadSuccess}
                                >
                                    <Page pageNumber={pageNumber} scale={2} />
                                </Document>

                                <div style={{ ...hoverElementsStyle, opacity: isHovering ? 1 : 0 }}>

                                    {pageNumber > 1 && (
                                        <div style={navButtonStyle} onClick={goToPrevPage}>
                                            <KeyboardArrowLeftIcon sx={{
                                                fontSize: 30,
                                                cursor: 'pointer',
                                                border: 'solid 1px black',
                                                borderRadius: '50%',
                                                color: 'white',
                                                margin: '10px 10px',
                                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                            }} />
                                        </div>
                                    )}

                                    {pageNumber < numPages && (
                                        <div style={{ ...navButtonStyle, right: 0 }} onClick={goToNextPage}>
                                            <KeyboardArrowRightIcon sx={{
                                                fontSize: 30,
                                                cursor: 'pointer',
                                                border: 'solid 1px black',
                                                borderRadius: '50%',
                                                color: 'white',
                                                margin: '10px 10px',
                                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                            }} />
                                        </div>
                                    )}

                                    <div style={pageTitleStyle} >
                                        <span style={titlePages}>{numPages} Pages</span>
                                    </div>

                                    <div style={pageIndicatorStyle}>
                                        Page {pageNumber} of {numPages}
                                    </div>
                                </div>
                            </div>
                        </Box>
                    )}
                    {/* Render Link Preview */}
                </Box>

                {/* Reaction, Comments, and Reposts */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 1, // Adjust the margin as needed
                    p: '10px 10px',
                }}>
                    <Box className='text-xs'>
                        <span role="img" aria-label="like icon">👍</span>
                        <span role="img" aria-label="insight icon">🔍</span>
                        <span role="img" aria-label="fullove icon">❤️</span>
                        281 Reactions
                    </Box>
                    <Box className='text-xs'>
                        <span style={{ marginRight: '0.5rem' }}>7 comments</span>

                        <span style={{ marginRight: '0.5rem' }}>•</span>
                        <span style={{ marginRight: '0.5rem' }}>1 repost</span>
                    </Box>
                </Box>
                <Divider sx={{ mb: 1, m: '0px 0px' }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '10px 10px' }}>
                    <IconButton sx={{ cursor: 'pointer', gap: 1 }}>
                        <ThumbUpIcon sx={{ color: '#9d9b9b', rotate: '0deg' }} />
                        <Typography level="body-xs">Like</Typography>
                    </IconButton>

                    <IconButton sx={{ cursor: 'pointer', gap: 1 }}>
                        <ChatIcon sx={{ color: '#9d9b9b', rotate: '0deg' }} />
                        <Typography level="body-xs">Comment</Typography>
                    </IconButton>

                    <IconButton sx={{ cursor: 'pointer', gap: 1 }}>
                        <RepeatIcon sx={{ color: '#9d9b9b', rotate: '0deg' }} />
                        <Typography level="body-xs">Repost</Typography>
                    </IconButton>

                    <IconButton sx={{ cursor: 'pointer', gap: 0 }}>
                        <SendIcon sx={{ color: '#9d9b9b', rotate: '-50deg' }} />
                        <Typography level="body-xs">Send</Typography>
                    </IconButton>

                </Box>
            </Box>
        </Box>
    );
}


const viewerContainerStyle = {
    position: 'relative',
    width: '100%',
    height: 'auto',
    marginTop: '1rem',
}

const hoverElementsStyle = {
    transition: 'opacity 0.9s ease', // Transition effect
    opacity: 0, // Initial opacity


    // ... other styles ...
};

const pageTitleStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    color: 'white',
    padding: '8px 15px',
    width: '100%',
    fontSize: '15px',
    textAlign: 'center',
    lineHeight: '1.5',
    fontWeight: 'bold',
};

const titlePages = {
    fontSize: '14px',
    fontWeight: 'normal',
}

const navButtonStyle = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
    zIndex: 1000,
    color: 'white',
    padding: '0px 0px',
    margin: '0px 0px',
    borderRadius: '50%',
};

const pageIndicatorStyle = {
    position: 'absolute',
    bottom: 0,
    width: '100%',
    textAlign: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    color: 'white',
    padding: '5px',
    fontSize: '14px',
};


export default PreviewPost;
